'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { FiSend } from 'react-icons/fi';
import { MdGridView } from 'react-icons/md';

import { IoMdClose } from 'react-icons/io';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);

  return (
    <>
      <Sheet>
        <SheetTrigger
          className={cn(
            'flex items-center justify-end gap-2 text-primary',
            'ea-mobile-sidebar__trigger',
            props.className?.trigger,
          )}
        >
          <MdGridView size={24} />
          MENU
        </SheetTrigger>
        <SheetContent
          aria-label="Navigácia"
          aria-labelledby={undefined}
          side={'right'}
          className={cn(
            'z-[100] flex w-screen flex-col items-center justify-start gap-12 border-none bg-white px-6 pt-24 shadow-none',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
        >
          <div className="absolute left-0 top-3 flex w-full items-center justify-between px-6">
            <Image
              src={generalLayout.footer?.logo.url || '/images/placeholder.png'}
              alt={resolveImageAlt(generalLayout?.footer?.logo, undefined)}
              width={85}
              height={35}
            />
            <SheetClose
              aria-label="Zavrieť menu"
              className="flex h-6 w-6 items-center justify-center rounded-[4px] border-[1.5px] border-primary"
            >
              <IoMdClose className="text-primary" />
            </SheetClose>
          </div>
          {!!props.menu && (
            <div className="flex w-full flex-col items-center justify-center gap-6">
              {props.menu?.map((item, index) => {
                const href = item.path ? getSafeHref(item.path) : undefined;
                const Comp = href ? (isRelativeHref(href) ? Link : 'a') : 'span';

                const linkProps = href
                  ? {
                      href,
                      prefetch: isRelativeHref(href) ? false : undefined,
                    }
                  : {};

                return (
                  <SheetClose key={index} asChild>
                    <Comp {...linkProps} className="flex w-full items-center justify-center">
                      <div className={cn('text-lg', 'text-center uppercase text-black')}>
                        {item.name}
                      </div>
                    </Comp>
                  </SheetClose>
                );
              })}
            </div>
          )}
          <SheetClose asChild>
            <Button asChild className="w-full">
              <Link prefetchOnHover href={'#kontakt'}>
                <FiSend className="mr-2 h-5 w-5" />
                NAPÍŠTE NÁM
              </Link>
            </Button>
          </SheetClose>
          <SocialIcons
            className={{
              wrapper: 'flex w-full justify-center gap-4',
              button: 'ea-mobile-sidebar__si-button',
              icon: 'fill-black stroke-black text-black',
            }}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
